import type { ConsumerApiClient } from '../client';

export interface ModerationFlag {
  category: string;
  confidence: number;
  action: string;
  message: string;
}

export interface ModerationResult {
  status: 'pass' | 'warn';
  flags: ModerationFlag[];
  thumbnail_url: string;
  moderation_result_id: string;
}

export interface MediaUploadResponse {
  id: string;
  s3Key: string;
  aiScanStatus: string;
  createdAt: string;
  moderation?: ModerationResult | null;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
}

/**
 * Transport-only media API. The full URI → Blob → PUT → confirm pipeline
 * differs enough between React Native (file:// URIs + RN fetch semantics)
 * and the browser (File/Blob from an <input type="file">) that hosts
 * compose these primitives themselves.
 *
 * Mobile keeps its existing `uploadMedia`/`uploadPhotoWithModeration`
 * wrappers; web composes `getUploadUrl` + `uploadFileBlob` + `confirmUpload`
 * directly from a File object.
 */
export interface MediaApi {
  /** Request a presigned S3 upload URL. */
  getUploadUrl(fileName: string, contentType: string): Promise<UploadUrlResponse>;

  /**
   * PUT a Blob (or File — which extends Blob) to a presigned S3 URL.
   * Does NOT go through the auth-aware client — the presigned URL is
   * self-authenticating.
   */
  uploadFileBlob(uploadUrl: string, blob: Blob, contentType: string): Promise<void>;

  /** Notify backend that the S3 object is in place; kicks off moderation. */
  confirmUpload(s3Key: string, contentType: string): Promise<MediaUploadResponse>;

  /** Override a `warn` moderation result so the media can proceed. */
  overrideModeration(moderationResultId: string): Promise<void>;
}

export function createMediaApi(client: ConsumerApiClient): MediaApi {
  return {
    getUploadUrl(fileName, contentType) {
      return client.post('/media/upload-url', { fileName, contentType });
    },

    async uploadFileBlob(uploadUrl, blob, contentType) {
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob,
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
      }
    },

    confirmUpload(s3Key, contentType) {
      return client.post('/media/confirm', { s3Key, contentType });
    },

    async overrideModeration(moderationResultId) {
      await client.patch(`/moderation/results/${moderationResultId}/override`, {
        action: 'proceed',
      });
    },
  };
}
